/**
 * API Client para comunicación con backend PHP
 * Todas las llamadas a la API pasan por aquí
 */

import { getSession, clearSession } from './session';

const API_BASE = import.meta.env.VITE_API_BASE || '';

/**
 * Wrapper para fetch que agrega autenticación y manejo de errores
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: Parameters<typeof fetch>[1] = {}
): Promise<T> {
  // Construir URL completa
  const url = `${API_BASE}${path}`;

  // Obtener sesión si existe
  const session = getSession();

  // Preparar headers
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  // Agregar Authorization si hay sesión (excepto para /portal/exchange)
  if (session && !path.includes('/portal/exchange')) {
    headers['Authorization'] = `Bearer ${session}`;
  }

  // Agregar Content-Type para JSON si hay body
  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  // Realizar petición
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Manejar 401: sesión inválida
    if (response.status === 401) {
      clearSession();
      // Redirigir al portal con mensaje de error
      if (typeof window !== 'undefined') {
        window.location.href = '/portal?error=session_expired';
      }
      throw new Error('Sesión expirada');
    }

    // Manejar otros errores HTTP
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    // Parsear respuesta JSON
    const data = await response.json();
    return data as T;
  } catch (error) {
    // Re-lanzar error para que el caller lo maneje
    throw error;
  }
}

/**
 * Helper para GET requests
 */
export async function apiGet<T = unknown>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'GET' });
}

/**
 * Helper para POST requests
 */
export async function apiPost<T = unknown>(
  path: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper para PUT requests
 */
export async function apiPut<T = unknown>(
  path: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper para DELETE requests
 */
export async function apiDelete<T = unknown>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}

/**
 * Helper para PATCH requests
 */
export async function apiPatch<T = unknown>(
  path: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}
